import { Observable } from 'rxjs';

/**
 * Id of the single hidden iframe reused for every browser-native download handoff.
 * Kept as the original literal so an in-flight download started by one caller is not
 * duplicated into a second frame by another.
 */
const DOWNLOAD_IFRAME_ID = 'oes-token-download-frame';

/**
 * Keep the "Starting download…" indicator visible long enough to register with the user.
 * The browser has taken over the download well before this timer fires — the extra delay is
 * purely UX padding, not functional.
 */
const HANDOFF_DELAY_MS = 3000;

/**
 * Parse a human-readable message out of an error document rendered into the download iframe.
 * Handles both JSON error bodies and plain-text/stack-trace bodies.
 */
export function extractDownloadErrorMessage(rawBody: string, title?: string): string {
  const fallbackMessage = title || 'Download failed. Please try again.';

  try {
    const parsed = JSON.parse(rawBody);
    return parsed?.error || parsed?.message || fallbackMessage;
  } catch {
    const lines = rawBody
      .split(/\r?\n/)
      .map((line) => line.replace(/\s+/g, ' ').trim())
      .filter(Boolean);
    const firstMeaningfulLine = lines.find((line) => !/^at\s/.test(line));
    return firstMeaningfulLine || fallbackMessage;
  }
}

/**
 * Shared iframe-based download helper. Points a hidden iframe at the given URL and
 * resolves after a short handoff delay. If the server returns an error page instead of
 * initiating a native browser download, the helper surfaces the parsed error message.
 *
 * <p>The URL passed here must already be directly fetchable by the browser without an
 * `Authorization` header — i.e. a presigned S3 URL or a token-bearing query-string URL.
 * Iframe `src` navigation cannot carry a bearer token, so pointing this at a JWT-protected
 * API route would 401 before reaching storage. Callers that need a protected resource must
 * first make an authenticated `HttpClient` call to obtain a presigned URL, then pass that
 * presigned URL here.
 *
 * @param url browser-navigable download URL (presigned or token-bearing).
 * @param onLateError invoked with the parsed message when the iframe reports an error page
 *        *after* the observable has already settled successfully — at that point the
 *        observable can no longer error, so the message is surfaced out-of-band (the caller
 *        typically routes this to `EventService.error`).
 */
export function triggerIframeDownload(
  url: string,
  onLateError: (message: string) => void
): Observable<void> {
  return new Observable<void>((observer) => {
    let iframe = document.getElementById(DOWNLOAD_IFRAME_ID) as HTMLIFrameElement | null;
    if (!iframe) {
      iframe = document.createElement('iframe');
      iframe.id = DOWNLOAD_IFRAME_ID;
      iframe.style.display = 'none';
      document.body.appendChild(iframe);
    }

    let settled = false;
    let handoffTimer: number;

    const settleSuccess = () => {
      if (settled) {
        return;
      }
      settled = true;
      window.clearTimeout(handoffTimer);
      iframe.onload = null;
      observer.next();
      observer.complete();
    };

    const settleError = (message: string) => {
      window.clearTimeout(handoffTimer);
      if (!settled) {
        settled = true;
        iframe.onload = null;
        observer.error(new Error(message));
      } else {
        onLateError(message);
      }
      iframe.src = 'about:blank';
    };

    iframe.onload = () => {
      try {
        const iframeDocument = iframe.contentDocument || iframe.contentWindow?.document;
        const href = iframeDocument?.location?.href || '';
        const bodyText = iframeDocument?.body?.textContent?.trim() || '';
        const title = iframeDocument?.title?.trim() || '';

        if (href === 'about:blank') {
          return;
        }

        // Successful attachment downloads hand off to the browser without
        // rendering content into the iframe. If the iframe loads a document
        // instead, the backend returned an error page or message.
        if (bodyText || title) {
          settleError(extractDownloadErrorMessage(bodyText, title));
        }
      } catch {
        // Ignore cross-document inspection issues. This path is same-origin in normal use.
      }
    };

    iframe.src = url;

    handoffTimer = window.setTimeout(() => {
      settleSuccess();
    }, HANDOFF_DELAY_MS);

    return () => {
      settled = true;
      window.clearTimeout(handoffTimer);
      iframe.onload = null;
    };
  });
}
